import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SaveIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import CancelButton from '../../../../components/buttons/cancel-button.jsx';
import Error from '../../../../components/feedback/error.jsx';
import Loading from '../../../../components/feedback/loading.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { Checkbox } from '../../../../components/ui/checkbox.jsx';
import FormCard from '../../../../components/ui/form-card.jsx';
import FormFooter from '../../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form.jsx';
import { Input } from '../../../../components/ui/input.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { queryClient } from '../../../../services/query/client.js';
import { CATALYST_USER_ROLES } from '../../../../utils/constants.js';

export function StaffEdit() {
  const navigate = useNavigate();
  const { staffId } = useParams();

  const formEditUser = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      status: true,
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Nome é um campo obrigatório'),
        lastName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Apelido é um campo obrigatório'),
        email: yup
          .string()
          .email('Por favor insira um email válido')
          .required('Email é um campo obrigatório'),
        role: yup.string().required('Função / Role é um campo obrigatório'),
      }),
    ),
  });

  const queryStaff = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: staff } = await msCatalystApi.get(`/staff/${staffId}`);
      formEditUser.reset({ ...staff.data });
      return staff.data;
    },
  });

  const mutationEditUser = useMutation({
    mutationFn: async (data) => {
      const { data: updatedUser } = await msCatalystApi.put(
        `/users/${data?.userId}`,
        {
          firstName: data.firstName,
          lastName: data.lastName,
          fullName: `${data.firstName} ${data.lastName}`,
          status: data.status,
        },
      );

      return updatedUser.data;
    },
  });

  const editStaffMutation = useMutation({
    mutationFn: async (data) => {
      const { data: updatedStaff } = await msCatalystApi.put(
        `/staff/${staffId}`,
        {
          ...data,
          ROWID: staffId,
          fullName: `${data.firstName} ${data.lastName}`,
          role: data.role,
        },
      );

      return updatedStaff.data;
    },
  });

  const onSubmit = async (data) => {
    try {
      const editStaff = await editStaffMutation.mutateAsync(data);

      await mutationEditUser.mutateAsync(data);

      queryClient.setQueryData(['user'], editStaff);

      toast.success('Utilizador editado com sucesso!');

      navigate(`/backoffice/admin/staff/${staffId}`);
    } catch (err) {
      toast.error('Erro ao editar staff');
    }
  };

  if (queryStaff.isLoading) {
    return <Loading />;
  }

  if (queryStaff.isError) {
    return <Error />;
  }

  return (
    <FormUnsavedConfirmationWrapper form={formEditUser}>
      <div className="pb-6">
        <Heading className="pb-2">Editar Utilizador</Heading>
        <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
          {queryStaff.data.firstName} {queryStaff.data.lastName}
        </h2>
      </div>

      <Form {...formEditUser}>
        <form onSubmit={formEditUser.handleSubmit(onSubmit)}>
          <div className="flex flex-col space-y-10">
            <div className="flex flex-col gap-5">
              <FormCard className="grid grid-cols-1 gap-6 rounded-lg bg-zinc-100 p-10 dark:bg-zinc-900 lg:grid-cols-2">
                <FormField
                  control={formEditUser.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Nome</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditUser.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Apelido</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditUser.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Email</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditUser.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Função / Role</FormLabel>
                      <FormControl>
                        <Select
                          disabled={formEditUser.formState.isSubmitting}
                          onValueChange={field.onChange}
                          value={field.value}
                          defaultValue="Utilizador"
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.keys(CATALYST_USER_ROLES).map((key, i) => {
                              if (key === 'customer') return null;
                              return (
                                <SelectItem
                                  key={i}
                                  value={CATALYST_USER_ROLES[key]}
                                >
                                  {CATALYST_USER_ROLES[key]}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formEditUser.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Estado</FormLabel>
                      <div className="flex h-10 flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            disabled={formEditUser.formState.isSubmitting}
                            id="status"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <span className="space-y-0 text-sm">Ativo</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>
          </div>

          <FormFooter>
            <CancelButton />
            <Button
              type="submit"
              disabled={
                formEditUser.formState.isSubmitting ||
                !formEditUser.formState.isDirty
              }
              isLoading={
                formEditUser.formState.isSubmitting || formEditUser.isPending
              }
            >
              <SaveIcon />
              Guardar Alterações
            </Button>
          </FormFooter>
        </form>
      </Form>
    </FormUnsavedConfirmationWrapper>
  );
}
